import {hitFacebookPixelEvent} from "../facebook/hit_facebook_pixel";
import {hitGtmEvent} from "./track_event";

export type RegistrationMethod = "email";
export type UserType = "private" | "dealer";
export type ContactMethod = "show_phone" | "call" | "message" | "email";

let registerStartFired = false;
let offerFormStartFired = false;

/** Once per page session — tab to register or first field interaction. */
export const hitRegisterStartEvent = () => {
    if (registerStartFired) {
        return;
    }
    registerStartFired = true;
    hitGtmEvent("register_start");
};

/** Confirmed account creation only (after successful API response). */
export const hitSignUpEvent = (params: {method: RegistrationMethod; user_type?: UserType}) => {
    hitGtmEvent("sign_up", {
        method: params.method,
        ...(params.user_type ? {user_type: params.user_type} : {})
    });
    // Pixel is loaded by GTM; fire the standard Meta event from the confirmed sign_up path.
    hitFacebookPixelEvent("CompleteRegistration");
};

/** Logged-in user opens / starts the add-offer form — once per page session. */
export const hitOfferFormStartEvent = () => {
    if (offerFormStartFired) {
        return;
    }
    offerFormStartFired = true;
    hitGtmEvent("offer_form_start");
};

/** Confirmed offer creation only (after successful API response). */
export const hitOfferAddedEvent = (params: {
    offer_id?: string | number;
    brand?: string;
    model?: string;
    province?: string;
    city?: string;
    seller_type?: UserType | string;
}) => {
    hitGtmEvent("offer_added", {
        ...(params.offer_id != null && params.offer_id !== "" ? {offer_id: String(params.offer_id)} : {}),
        ...(params.brand ? {brand: params.brand} : {}),
        ...(params.model ? {model: params.model} : {}),
        ...(params.province ? {province: params.province} : {}),
        ...(params.city ? {city: params.city} : {}),
        ...(params.seller_type ? {seller_type: params.seller_type} : {})
    });
};

/** Contact CTA on offer detail (show phone / call / message / email). */
export const hitContactSellerEvent = (params: {
    offer_id: string | number;
    contact_method: ContactMethod;
    seller_type?: UserType | string;
}) => {
    if (params.offer_id == null || params.offer_id === "") {
        return;
    }

    hitGtmEvent("contact_seller", {
        offer_id: String(params.offer_id),
        contact_method: params.contact_method,
        ...(params.seller_type ? {seller_type: params.seller_type} : {})
    });
};

/** Confirmed login only (after successful auth). */
export const hitLoginEvent = (params: {method: RegistrationMethod | string}) => {
    hitGtmEvent("login", {
        method: params.method
    });
};

/** Best-effort extraction of created offer id from add-car API response. */
export const extractOfferIdFromAddCarResponse = (res: unknown): string | undefined => {
    if (!res || typeof res !== "object") {
        return undefined;
    }

    const data = res as Record<string, unknown>;
    const candidates = [data.id, data.offer_id, data.post_id, data.ID];

    for (const candidate of candidates) {
        if (candidate != null && candidate !== "") {
            return String(candidate);
        }
    }

    if (data.data && typeof data.data === "object") {
        return extractOfferIdFromAddCarResponse(data.data);
    }

    return undefined;
};
